import { STAFF_POSITION_STATUSES, type StaffPositionStatus } from '@hr/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { NetworkIcon, PlusIcon } from 'lucide-react';
import { z } from 'zod';
import { Combobox } from '@/components/combobox';
import { EmptyState, PageHeader } from '@/components/page';
import { SearchInput } from '@/components/search-input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { StaffPositionSheet } from '@/features/registry/staff-position-sheet';
import { StaffPositionsTable } from '@/features/registry/staff-positions-table';
import { useRegistry } from '@/features/registry/use-registry';
import { STAFF_POSITION_STATUS_LABELS, uk } from '@/i18n/uk';
import { useReferences } from '@/lib/references';
import { isHr } from '@/lib/roles';
import { listSearchShape, tableState, tableStateToSearch } from '@/lib/search-params';
import { trpc } from '@/lib/trpc';

const searchSchema = z.object({
  ...listSearchShape,
  project: z.uuid().optional().catch(undefined),
  center: z.uuid().optional().catch(undefined),
  location: z.uuid().optional().catch(undefined),
  status: z.enum(STAFF_POSITION_STATUSES).optional().catch(undefined),
  /** Відкрита штатна одиниця: uuid або 'new'. */
  sp: z
    .union([z.uuid(), z.literal('new')])
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute('/_app/staffing')({
  validateSearch: searchSchema,
  component: StaffingPage,
});

function StaffingPage() {
  const t = uk.registry;
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { me } = Route.useRouteContext();
  const canWrite = isHr(me.role);
  const registry = useRegistry();
  const refs = useReferences();
  const state = tableState(search, { sort: 'project', dir: 'asc' });
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const list = useQuery({
    ...trpc.staffPositions.list.queryOptions({
      page: state.page,
      pageSize: state.pageSize,
      sort: state.sort,
      dir: state.dir,
      q: search.q,
      projectId: search.project,
      centerId: search.center,
      locationId: search.location,
      status: search.status,
    }),
    placeholderData: keepPreviousData,
  });
  const hasFilters = Boolean(
    search.q || search.project || search.center || search.location || search.status,
  );

  return (
    <>
      <PageHeader
        title={t.staffing}
        description={t.staffingDescription}
        actions={
          canWrite && (
            <Button onClick={() => setSearch({ sp: 'new' })}>
              <PlusIcon /> {t.newStaffPosition}
            </Button>
          )
        }
      />
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <SearchInput
          value={search.q ?? ''}
          placeholder={t.searchPlaceholder}
          onChange={(q) => setSearch({ q: q || undefined, page: undefined })}
        />
        <Combobox
          className="w-48"
          value={search.project}
          onChange={(v) => setSearch({ project: v ?? undefined, page: undefined })}
          options={(registry.data?.projects ?? []).map((p) => ({
            value: p.id,
            label: p.code,
            hint: p.name,
          }))}
          placeholder={t.project}
          allowClear
        />
        <Combobox
          className="w-48"
          value={search.center}
          onChange={(v) => setSearch({ center: v ?? undefined, page: undefined })}
          options={(registry.data?.centers ?? []).map((c) => ({ value: c.id, label: c.name }))}
          placeholder={t.center}
          allowClear
        />
        <Combobox
          className="w-40"
          value={search.location}
          onChange={(v) => setSearch({ location: v ?? undefined, page: undefined })}
          options={(refs.data?.locations ?? []).map((l) => ({ value: l.id, label: l.name }))}
          placeholder={t.location}
          allowClear
        />
        <Select
          value={search.status ?? 'all'}
          onValueChange={(v) =>
            setSearch({
              status: v === 'all' ? undefined : (v as StaffPositionStatus),
              page: undefined,
            })
          }
        >
          <SelectTrigger className="w-40" aria-label={t.status}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t.status}: {uk.common.all.toLowerCase()}
            </SelectItem>
            {STAFF_POSITION_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {STAFF_POSITION_STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button
            variant="ghost"
            onClick={() =>
              setSearch({
                q: undefined,
                project: undefined,
                center: undefined,
                location: undefined,
                status: undefined,
                page: undefined,
              })
            }
          >
            {t.resetFilters}
          </Button>
        )}
      </div>
      <StaffPositionsTable
        data={list.data?.items}
        total={list.data?.total ?? 0}
        state={state}
        onStateChange={(next) => setSearch(tableStateToSearch(next))}
        isLoading={list.isPending}
        onOpen={(id) => setSearch({ sp: id })}
        empty={
          <EmptyState
            icon={NetworkIcon}
            title={hasFilters ? uk.common.notFound : t.emptyStaffing}
            hint={hasFilters ? undefined : t.emptyStaffingHint}
          />
        }
      />
      <StaffPositionSheet
        id={search.sp}
        projectId={search.project}
        canWrite={canWrite}
        onOpenChange={(o) => !o && setSearch({ sp: undefined })}
      />
    </>
  );
}
